import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertCircle } from 'lucide-react';

const UGEDAGE = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
const TIMER = Array.from({ length: 24 }, (_, i) => i);

function heatColor(antal, maxAntal) {
  if (!antal || maxAntal === 0) return 'rgba(255,255,255,0.04)';
  const intensity = Math.min(antal / maxAntal, 1);
  const r = Math.round(35 + intensity * (59 - 35));
  const g = Math.round(130 + intensity * (200 - 130));
  const b = Math.round(246);
  return `rgba(${r},${g},${b},${0.2 + intensity * 0.8})`;
}

export default function BeregnerHeatmapPanelIV() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    fetch('/api/os/vaekst/beregner-heatmap', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  if (loading) return <div style={{ color: '#9ca3af', padding: 24 }}>Henter heatmap-data...</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 24, display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={16} /> {error}</div>;

  const { heatmap, typer } = data;

  // Aggreger heatmap data
  const grid = {};
  (heatmap || []).forEach(r => {
    if (filter !== 'alle' && r.beregner_type !== filter) return;
    const key = `${r.ugedag}-${r.time}`;
    grid[key] = (grid[key] || 0) + r.antal;
  });

  const maxAntal = Math.max(...Object.values(grid), 1);

  // Tooltip dominerende type
  function getDomType(ugedag, time) {
    const rel = (heatmap || []).filter(r => r.ugedag === ugedag && r.time === time);
    if (!rel.length) return 'Ingen data';
    const best = rel.reduce((a, b) => a.antal > b.antal ? a : b);
    return `${best.beregner_type} (${best.antal})`;
  }

  const alleTyper = [...new Set((heatmap || []).map(r => r.beregner_type))];

  return (
    <div style={{ width: '100%' }}>
      {/* Filter-knapper */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {['alle', ...alleTyper].map(t => (
          <button key={t} onClick={() => setFilter(t)}
            style={{ padding: '5px 12px', borderRadius: 16, border: `1px solid ${filter === t ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`, background: filter === t ? 'rgba(59,130,246,0.2)' : 'transparent', color: filter === t ? '#3b82f6' : '#9ca3af', fontSize: 12, cursor: 'pointer' }}>
            {t === 'alle' ? 'Alle typer' : t}
          </button>
        ))}
      </div>

      {/* Heatmap */}
      <div style={{ overflowX: 'auto', position: 'relative' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `50px repeat(24, minmax(20px, 1fr))`, gap: 2, minWidth: 600 }}>
          {/* Header timer */}
          <div />
          {TIMER.map(t => (
            <div key={t} style={{ color: '#6b7280', fontSize: 10, textAlign: 'center', paddingBottom: 4 }}>
              {t % 4 === 0 ? `${t}h` : ''}
            </div>
          ))}
          {/* Rækker per ugedag */}
          {UGEDAGE.map((dag, d) => (
            <React.Fragment key={d}>
              <div style={{ color: '#9ca3af', fontSize: 12, display: 'flex', alignItems: 'center', paddingRight: 8 }}>{dag}</div>
              {TIMER.map(t => {
                const key = `${d}-${t}`;
                const antal = grid[key] || 0;
                return (
                  <div key={t}
                    onMouseEnter={e => setTooltip({ x: e.clientX, y: e.clientY, dag, time: t, antal, dom: getDomType(d, t) })}
                    onMouseLeave={() => setTooltip(null)}
                    style={{ height: 24, borderRadius: 3, background: heatColor(antal, maxAntal), cursor: antal > 0 ? 'pointer' : 'default', transition: 'opacity 0.15s' }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 30, background: '#1e2235', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 12, pointerEvents: 'none', zIndex: 9999 }}>
          {tooltip.dag} {tooltip.time}:00 — {tooltip.antal} sessioner<br />
          Dominerende: {tooltip.dom}
        </div>
      )}

      {/* Søjlediagram */}
      {typer && typer.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>Fordeling per beregner-type</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={typer} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="type" tick={{ fill: '#9ca3af', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1e2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} />
              <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Sessioner" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
